import { useState } from "react";
import styles from "./pricing.module.css";
import { useBreakpoint } from "../../../../../hooks/useMediaQuery";
import { useNavigate } from "react-router";

const COPY = {
  USD: {
    packFrom: "from $100",
    annualFrom: "from $360/yr",
    annualTrust: "Annual plans from $360/yr",
  },
  NGN: {
    packFrom: "from ₦35,000",
    annualFrom: "from ₦120,000/yr",
    annualTrust: "Annual plans from ₦120,000/yr",
  },
};

const PricingCallout = ({ defaultCurrency = "USD" }) => {
  const [currency, setCurrency] = useState(defaultCurrency);
  const navigate = useNavigate();
  const width = useBreakpoint();
  const isMobile = width < 640;
  const copy = COPY[currency];

  const stats = [
    { value: copy.packFrom, label: "credit packs", color: "#22C55E" },
    { value: copy.annualFrom, label: "annual plans", color: "#8B5CF6" },
    { value: "never expire", label: "unused credits", color: "#F1F5FF" },
  ];

  const trust = [
    "🛡️  30-day rematch guarantee",
    `📆  ${copy.annualTrust}`,
  ];

  return (
    <section
      id={styles["pricing-callout"]}
      aria-label="Pricing overview"
      className="bg-[#07080F]"
      style={{
        padding: isMobile ? "64px 20px" : "96px 24px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div className="max-w-[640px] mx-auto text-center">
        {/* ── Eyebrow ── */}
        <div
          className="inline-flex items-center gap-[7px] py-[5px] px-4 rounded-[20px] mb-5 text-[11px] font-bold uppercase text-[#4F7EF7]"
          style={{
            background: "rgba(79,126,247,0.08)",
            border: "1px solid rgba(79,126,247,0.2)",
            letterSpacing: "0.1em",
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#4F7EF7] inline-block shrink-0" />
          Transparent Pricing
        </div>

        {/* ── Currency toggle ─── */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex gap-[3px] bg-[#0C1422] border border-[#1A2540] rounded-lg p-[3px]">
            {[
              { v: "USD", l: "$ USD" },
              { v: "NGN", l: "₦ NGN" },
            ].map((opt) => (
              <button
                key={opt.v}
                className="awpc-curr text-xs font-bold rounded-md cursor-pointer min-h-9"
                onClick={() => setCurrency(opt.v)}
                aria-pressed={currency === opt.v}
                style={{
                  padding: "7px 18px",
                  background: currency === opt.v ? "#4F7EF7" : "transparent",
                  border: "none",
                  color: currency === opt.v ? "#fff" : "#8898B5",
                  fontFamily: "inherit",
                  transition: "background 0.15s",
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {/* ── Headline ──────────── */}
        <h2
          className="font-extrabold text-[#F1F5FF] mb-4"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: isMobile ? "26px" : "38px",
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Pay for hires,
          <br />
          not for a subscription
        </h2>

        {/* ── Subheadline ────────── */}
        <p
          className="text-[#8898B5] mb-9"
          style={{
            fontSize: isMobile ? "14px" : "15px",
            lineHeight: 1.65,
          }}
        >
          Credits flex with the role you're hiring for. Buy a one-off pack when
          you need to hire, or commit annually and pay up to 60% less per
          credit.
        </p>

        {/* ── Key stats ──────────── */}
        <div
          className="flex items-center justify-center mb-9"
          style={{
            flexDirection: isMobile ? "column" : "row",
            gap: "0",
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="flex items-center"
              style={{
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              {/* Stat block */}
              <div
                className="text-center"
                style={{
                  padding: isMobile ? "14px 0" : "0 16px",
                  borderTop: isMobile && i > 0 ? "1px solid #1A2540" : "none",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <div
                  className="font-extrabold"
                  style={{
                    fontSize: isMobile ? "22px" : "26px",
                    color: stat.color,
                    letterSpacing: "-0.02em",
                    fontFamily: "'Space Grotesk', sans-serif",
                    lineHeight: 1.1,
                  }}
                >
                  {stat.value}
                </div>

                <div className="text-[11px] text-[#4A5A7A] mt-1 font-semibold">
                  {stat.label}
                </div>
              </div>

              {/* Dot separator — desktop only */}
              {!isMobile && i < stats.length - 1 && (
                <span className="text-[#1A2540] text-[22px] leading-none shrink-0">
                  ·
                </span>
              )}
            </div>
          ))}
        </div>

        {/* ── Primary CTA ────────── */}
        <div className="mb-6">
          <button
            className="awpc-cta inline-flex items-center gap-2 py-[14px] px-8 bg-[#4F7EF7] rounded-[10px] text-white text-[15px] font-bold justify-center min-h-[52px]"
            onClick={() => navigate("/pricing")}
            style={{
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: "-0.01em",
              transition: "background 0.15s",
              width: isMobile ? "100%" : "auto",
            }}
          >
            See full pricing
            <span className="text-lg leading-none">→</span>
          </button>
        </div>

        {/* ── Trust badges ───────── */}
        <div
          className="flex items-center justify-center"
          style={{
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "10px" : "0",
          }}
        >
          {trust.map((item, i) => (
            <div key={i} className="flex items-center">
              <span className="text-xs text-[#4A5A7A] font-medium">{item}</span>

              {!isMobile && i < trust.length - 1 && (
                <span className="text-[#1A2540] mx-[14px] text-sm">·</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingCallout;
